<template>
  <v-text-field
    ref="inputRef"
    v-model="displayValue"
    v-bind="$attrs"
    :label="label"
    :placeholder="placeholder"
    :rules="computedRules"
    :error-messages="errorMessages"
    @input="handleInput"
    @blur="handleBlur"
    @focus="handleFocus"
  >
    <template #prepend-inner>
      <span class="text-body-2 text-medium-emphasis mr-2">+1</span>
    </template>
  </v-text-field>
</template>

<script setup lang="ts">
/**
 * US phone number input that displays +1 formatting in the field and emits
 * values in E.164 format for API calls.
 */
import { ref, computed, watch } from "vue";
import {
  formatPhoneNumber,
  toE164,
  fromE164,
  getPhoneValidationRules,
} from "./phone-utils.ts";

interface Props {
  /** v-model value in E.164 format (for example, `+15551234567`). */
  modelValue?: string;
  /** Additional Vuetify validation rules appended after the built-in phone rules. */
  rules?: Array<(value: string) => string | boolean>;
  /** When true, the built-in phone validation treats the field as required. */
  required?: boolean;
  /** Label shown on the underlying `v-text-field`. */
  label?: string;
  /** Placeholder shown when the field is empty. */
  placeholder?: string;
  /** External error messages passed through to `v-text-field`. */
  errorMessages?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  rules: () => [],
  required: false,
  label: "Phone Number",
  placeholder: "(555) 123-4567",
  errorMessages: () => [],
});

const emit = defineEmits<{
  /** Emitted when the E.164 value changes. */
  "update:modelValue": [value: string];
}>();

const displayValue = ref("");
const isFocused = ref(false);
const inputRef = ref<{ focus: () => void } | null>(null);

defineExpose({
  /** Focus the underlying Vuetify text field. */
  focus() {
    inputRef.value?.focus();
  },
});

// Handle input changes
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;

  // Only allow digits, parentheses, spaces, and hyphens
  const cleaned = value.replace(/[^\d\s()-]/g, "");

  const formatted = formatPhoneNumber(cleaned);
  displayValue.value = formatted;

  // Emit E.164 format
  const e164 = toE164(formatted);
  emit("update:modelValue", e164);
};

const handleFocus = () => {
  isFocused.value = true;
};

const handleBlur = () => {
  isFocused.value = false;
};

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && newValue !== toE164(displayValue.value)) {
      displayValue.value = fromE164(newValue);
    }
  },
  { immediate: true },
);

// Validation rules
const phoneValidationRules = getPhoneValidationRules(props.required);

const computedRules = computed(() => {
  return [...phoneValidationRules, ...props.rules];
});
</script>
